import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { listObjectsInFolder } from '../../utils/storageUtils';
import { fetchCollectionDocIds, fetchFirestoreCollections } from '../../utils/firestoreUtils';
import { formatFileSize } from '../../utils/format';
import { useAlert } from '../../contexts/ToastContext';

Modal.setAppElement("#root");

export default function DeleteModal({
  isOpen,
  onRequestClose,
  onConfirm,
  onConfirmMulti,
  deleteTarget,
  itemType = "item",
  requirePassword = true,
  submitting = false,
  context = null,
  showMultiDeleteOption = false,
}) {
  const [password, setPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const showAlert = useAlert();
  const [isMultiDeleteMode, setIsMultiDeleteMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [availableItems, setAvailableItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setIsPasswordValid(false);
      setIsMultiDeleteMode(false);
      setSelectedItems([]);
      setSelectAll(true);
      setAvailableItems([]);
      
      if (showMultiDeleteOption && context) {
        loadAvailableItems();
      }
    }
  }, [isOpen, context, showMultiDeleteOption]);

  // Load available files/documents based on context
  const loadAvailableItems = async () => {
    setLoadingItems(true);
    try {
      let items = [];
      
      if (context.type === 'store') {
        // For store: get files from current folder
        const result = await listObjectsInFolder(context.folderPath || '', context.bucketName);
        
        items = result.items.map(item => ({
          id: item.name, // Keep full path as ID for deletion
          displayName: item.name.split('/').pop(), // Show only base name for display
          fullPath: item.name,
          size: item.size ? formatFileSize(item.size) : 'N/A',
        }));
        
      } else if (context.type === 'db') {
        // For database: get all collection names so the user can choose which to delete
        const collections = await fetchFirestoreCollections();

        items = collections.map(col => ({
          id: col,
          displayName: col,
          fullPath: col,
        }));
      }
      
      setAvailableItems(items);
      // Pre-select all items in multi-delete mode
      setSelectedItems(items.map(item => item.id));
      setSelectAll(true);
      
    } catch (error) {
      console.error('Error loading items for multi-delete:', error);
      setAvailableItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setIsPasswordValid(newPassword === "admin123");
  };

  const handleSingleConfirm = () => {
    if (requirePassword && !isPasswordValid) {
      showAlert("Please enter a valid admin password");
      return;
    }
    onConfirm();
  };

  const handleMultiConfirm = () => {
    if (requirePassword && !isPasswordValid) {
      showAlert("Please enter a valid admin password");
      return;
    }
    if (selectedItems.length === 0) {
      showAlert("Please select at least one item to delete");
      return;
    }
    onConfirmMulti(selectedItems);
  };

  const handleItemSelect = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
      setSelectAll(false);
    } else {
      setSelectedItems([...selectedItems, itemId]);
      if (selectedItems.length + 1 === availableItems.length) {
        setSelectAll(true);
      }
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      setSelectedItems(availableItems.map(item => item.id));
      setSelectAll(true);
    }
  };

  const handleClose = () => {
    onRequestClose();
  };

  const switchToMultiDelete = () => {
    setIsMultiDeleteMode(true);
  };

  const switchToSingleDelete = () => {
    setIsMultiDeleteMode(false);
  };

  const getDisplayName = () => {
    if (typeof deleteTarget === 'string') return deleteTarget;
    if (deleteTarget && typeof deleteTarget === 'object') {
      return deleteTarget.name || 'item';
    }
    return 'item';
  };

  // Render multi-delete interface
  const renderMultiDeleteInterface = () => (
    <>
      <div className="dfm-field">
        <p className="dfm-delete-warning">
          Select items to delete from <strong>{context?.displayName || getDisplayName()}</strong>:
        </p>
        
        {loadingItems ? (
          <div className="multi-delete-loading">
            Loading items...
          </div>
        ) : (
          <>
            {/* Select All Checkbox */}
            {availableItems.length > 0 && (
              <div className="multi-delete-select-all">
                <input
                  type="checkbox"
                  id="selectAllItems"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  disabled={submitting}
                />
                <label htmlFor="selectAllItems">
                  <strong>Select All ({availableItems.length} items)</strong>
                </label>
              </div>
            )}

            {/* Items List - UPDATED: using displayName instead of name */}
            <div className="multi-delete-items-list">
              {availableItems.length > 0 ? (
                availableItems.map((item, index) => (
                  <div key={item.id} className="multi-delete-item">
                    <input
                      type="checkbox"
                      id={`item-${index}`}
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleItemSelect(item.id)}
                      disabled={submitting}
                    />
                    <label htmlFor={`item-${index}`}>
                      {item.displayName} {/* Changed from item.name to item.displayName */}
                      {item.size && <span className="item-size"> - {item.size}</span>}
                    </label>
                  </div>
                ))
              ) : (
                <div className="multi-delete-empty">
                  No items found in {context?.type === 'store' ? 'folder' : 'Firestore'}.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {requirePassword && (
        <div className="dfm-field">
          <label htmlFor="adminPassword" className="dfm-label">
            Admin Password
            <span className="dfm-required">*</span>
          </label>
          <input
            type="password"
            id="adminPassword"
            value={password}
            onChange={handlePasswordChange}
            className="dfm-input"
            placeholder="Enter admin password to confirm"
            disabled={submitting}
          />
          {password && !isPasswordValid && (
            <small className="dfm-error">Invalid password</small>
          )}
        </div>
      )}
    </>
  );

  // Render single delete interface
  const renderSingleDeleteInterface = () => (
    <>
      <div className="dfm-field">
        <p className="dfm-delete-warning">
          Delete <strong>"{getDisplayName()}"</strong>?
        </p>
      </div>

      {requirePassword && (
        <div className="dfm-field">
          <label htmlFor="adminPassword" className="dfm-label">
            Admin Password
            <span className="dfm-required">*</span>
          </label>
          <input
            type="password"
            id="adminPassword"
            value={password}
            onChange={handlePasswordChange}
            className="dfm-input"
            placeholder="Enter admin password to confirm"
            disabled={submitting}
          />
          {password && !isPasswordValid && (
            <small className="dfm-error">Invalid password</small>
          )}
        </div>
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className={`dfm-modal delete-modal ${isMultiDeleteMode ? 'multi-delete-mode' : ''}`}
      overlayClassName="dfm-overlay"
      shouldCloseOnOverlayClick={!submitting}
      shouldCloseOnEsc={!submitting}
    >
      <div className="dfm-header">
        <h3 className="dfm-title">
          {isMultiDeleteMode ? 'Delete Multiple Items' : 'Confirm Deletion'}
        </h3>
        <button 
          className="dfm-close" 
          onClick={handleClose}
          disabled={submitting}
        >
          ×
        </button>
      </div>

      <div className="dfm-body">
        {isMultiDeleteMode 
          ? renderMultiDeleteInterface()
          : renderSingleDeleteInterface()
        }
      </div>

      <div className="dfm-footer">
        {/* Back button in multi-delete mode */}
        {isMultiDeleteMode && (
          <button
            className="dfm-btn dfm-btn-secondary"
            onClick={switchToSingleDelete}
            disabled={submitting}
          >
            Back
          </button>
        )}
        
        {/* Cancel button */}
        <button
          className="dfm-btn dfm-btn-secondary"
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </button>

        {/* Action buttons */}
        {isMultiDeleteMode ? (
          <button
            className="dfm-btn dfm-btn-danger"
            onClick={handleMultiConfirm}
            disabled={submitting || selectedItems.length === 0 || (requirePassword && !isPasswordValid)}
          >
            {submitting ? "Deleting..." : `Delete ${selectedItems.length} Item(s)`}
          </button>
        ) : (
          <>
            {showMultiDeleteOption && (
              <button
                className="dfm-btn dfm-btn-warning"
                onClick={switchToMultiDelete}
                disabled={submitting || (requirePassword && !isPasswordValid)}
              >
                Delete Multiple
              </button>
            )}
            <button
              className="dfm-btn dfm-btn-danger"
              onClick={handleSingleConfirm}
              disabled={submitting || (requirePassword && !isPasswordValid)}
            >
              {submitting ? "Deleting..." : "Delete"}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}